import { Stack, Typography } from '@mui/material';
import { useEffect } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FamilyTreeWarningType } from '@/types/schema';

import { useFamilyTreeContext } from '../contexts';

export function useFamilyTreeWarnings() {
  const { showFeedback } = useFeedback();
  const { state } = useFamilyTreeContext();

  // Display warning toast for critical family tree issues
  useEffect(() => {
    if (!state.familyTreeData?.warnings || state.presentationMode) return;

    const criticalWarningTypes = [
      FamilyTreeWarningType.GenerationConflict,
      FamilyTreeWarningType.MultipleBiologicalParents,
      FamilyTreeWarningType.MultipleCurrentSpouses,
      FamilyTreeWarningType.RelationshipCycle,
    ];

    const criticalWarnings = state.familyTreeData.warnings.filter((warning) =>
      criticalWarningTypes.includes(warning.type)
    );

    // Dedupe by message and take the first one
    const uniqueMessages = new Set<string>();
    const firstUniqueWarning = criticalWarnings.find((warning) => {
      if (uniqueMessages.has(warning.message)) {
        return false;
      }
      uniqueMessages.add(warning.message);
      return true;
    });

    const firstUniqueWarningClientProfiles =
      state.familyTreeData.people?.filter((edge) =>
        firstUniqueWarning?.clientProfileIds.includes(
          edge.clientProfile.id ?? ''
        )
      );

    const warningNames = firstUniqueWarningClientProfiles
      ?.map((profile) => profile.clientProfile.displayName)
      .join(', ');

    const formattedWarning = firstUniqueWarning ? (
      <Stack spacing={1}>
        <Typography variant="body-s">{firstUniqueWarning.message}</Typography>
        {warningNames && (
          <Typography variant="body-xs">{warningNames}</Typography>
        )}
      </Stack>
    ) : undefined;

    // Show only the first critical warning
    if (formattedWarning) {
      showFeedback(formattedWarning, {
        variant: 'warning',
        persist: true, // Keep visible until user dismisses
      });
    }
  }, [
    showFeedback,
    state.familyTreeData?.people,
    state.familyTreeData?.warnings,
    state.presentationMode,
  ]);
}
